import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton, Iskelet, Kart, Metin } from "@koclukpanel/ui";
import { ApiHatasi, tanimaNotu, tanimaNotuYaz } from "../api";

const EN_COK = 4000;

/**
 * ÖĞRENCİ TANIMA NOTU — öğrenci 360° genel bakış (sürüm 9a; UNİKOÇ "Öğrenci Tanıma")
 *
 * Matris §5.6: personel görür (kapsamıyla), öğrenci ve veli GÖRMEZ. Bu bir
 * personel paneli bileşeni ve öğrenci/veli uygulamalarında karşılığı yok;
 * asıl kapı sunucuda (`kisi.not.goruntule`, SELF/LINKED kapsamı alamıyor).
 * Kart yalnız izin varken çiziliyor (GenelBakis), yani izinsiz kişi istek de atmıyor.
 *
 * Kayıt, EKRANDA GÖRÜLEN metinle koşullu: araya başka bir yazım girdiyse
 * sunucu 409 TANIMA_NOTU_DEGISTI verir; yazılan metin kaybolmasın diye kutuda kalır.
 *
 * 9A-03 (Codex) — BEKLENEN METİN DÜZENLEME BAŞINDA SABİTLENİR. Önceden her gönderimde güncel sorgu
 * verisinden alınıyordu: 409 sonrası sorgu yenilenince ikinci Kaydet, başkasının yeni metnini "gördüm"
 * diye gönderip onu eziyordu. Artık 409'da beklenen İLERLEMEZ; güncel metin gösterilir ve ilerlemek
 * yalnız kullanıcının açık seçimiyle ("Güncel metni yükle") olur.
 */
export function TanimaNotuKarti({
  ogrenciId,
  duzenleyebilir,
}: {
  ogrenciId: string;
  duzenleyebilir: boolean;
}) {
  const qc = useQueryClient();
  // `beklenen`: kullanıcının düzenlemeye başlarken GÖRDÜĞÜ metin — sunucuya koşul olarak gider.
  const [duzenleme, setDuzenleme] = useState<{ taslak: string; beklenen: string | null } | null>(
    null,
  );
  const taslak = duzenleme?.taslak ?? null;
  const not = useQuery({
    queryKey: ["tanima-notu", ogrenciId],
    queryFn: () => tanimaNotu(ogrenciId),
    retry: false,
  });

  const kaydet = useMutation({
    mutationFn: (g: { metin: string; gorulen: string | null }) =>
      tanimaNotuYaz(ogrenciId, g.metin.trim().length === 0 ? null : g.metin, g.gorulen),
    onSuccess: (yeni) => {
      qc.setQueryData(["tanima-notu", ogrenciId], yeni);
      setDuzenleme(null);
    },
    onError: (e) => {
      if (e instanceof ApiHatasi && e.kod === "TANIMA_NOTU_DEGISTI") {
        void qc.invalidateQueries({ queryKey: ["tanima-notu", ogrenciId] });
      }
    },
  });

  const cakisma =
    duzenleme !== null &&
    kaydet.error instanceof ApiHatasi &&
    kaydet.error.kod === "TANIMA_NOTU_DEGISTI";

  return (
    <Kart
      baslik="Öğrenci tanıma"
      aciklama="Yalnız personel görür — öğrenci ve veli göremez"
      eylem={
        duzenleyebilir && taslak === null && not.data !== undefined ? (
          <Buton
            gorunum="ikincil"
            onClick={() =>
              setDuzenleme({ taslak: not.data.metin ?? "", beklenen: not.data.metin ?? null })
            }
          >
            {not.data.metin ? "Düzenle" : "Not yaz"}
          </Buton>
        ) : undefined
      }
    >
      {not.isPending ? (
        <Iskelet satir={2} />
      ) : not.isError ? (
        <p role="alert" className="text-hata-700 text-sm">
          Tanıma notu alınamadı.
        </p>
      ) : duzenleme !== null ? (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            kaydet.mutate({ metin: duzenleme.taslak, gorulen: duzenleme.beklenen });
          }}
        >
          <Metin
            etiket="Tanıma notu"
            rows={5}
            maxLength={EN_COK}
            sayacGoster
            value={duzenleme.taslak}
            onChange={(e) => setDuzenleme({ ...duzenleme, taslak: e.target.value })}
            yardim="Öğrencinin ilgi alanları, çalışma alışkanlıkları, dikkat edilecek noktalar."
          />
          {kaydet.error instanceof ApiHatasi && (
            <p role="alert" className="text-hata-700 text-sm">
              {kaydet.error.message}
            </p>
          )}
          {cakisma && (
            <div className="border-notr-200 rounded-md border p-2 text-sm">
              <p className="text-notr-600">Güncel not:</p>
              <p className="text-notr-800 whitespace-pre-line">
                {not.data.metin ?? "(not silinmiş)"}
              </p>
              <Buton
                gorunum="ikincil"
                className="mt-2"
                onClick={() => {
                  // Açık seçim: kullanıcı güncel metni GÖRDÜ — beklenen ancak şimdi ilerler.
                  setDuzenleme({ taslak: not.data.metin ?? "", beklenen: not.data.metin ?? null });
                  kaydet.reset();
                }}
              >
                Güncel metni yükle
              </Buton>
            </div>
          )}
          <div className="flex gap-2">
            <Buton type="submit" yukleniyor={kaydet.isPending}>
              Kaydet
            </Buton>
            <Buton
              gorunum="sessiz"
              onClick={() => {
                setDuzenleme(null);
                kaydet.reset();
              }}
            >
              Vazgeç
            </Buton>
          </div>
        </form>
      ) : not.data.metin ? (
        <p className="text-notr-800 mt-1 text-sm whitespace-pre-line">{not.data.metin}</p>
      ) : (
        <p className="text-notr-600 mt-1 text-sm">Tanıma notu yazılmamış.</p>
      )}
    </Kart>
  );
}
